export type Direction = 'ai' | 'user';

export const MESSAGE_TYPES = {
  MESSAGE: 'message',
  OPTIMISTIC: 'optimistic',
  ERROR: 'error',
  SYSTEM: 'system',
  PROPOSAL: 'proposal',
  SEARCH_SUPPORT_INSIGHT: 'search_support_insight',
  UNSUPPORTED_REQUEST: 'unsupported_request',
  CALENDAR_CONNECTION_PROMPT: 'calendar_connection_prompt',
  CALENDAR_CONTEXT_SUMMARY: 'calendar_context_summary',
  SUMMARY: 'summary',
  RESERVATION_INFO_FORM: 'reservation_info_form',
  RESERVATION_INFO_CONFIRMED: 'reservation_info_confirmed',
  ARRANGEMENT_CONFIRMATION: 'arrangement_confirmation',
  ARRANGEMENT_COMPLETED: 'arrangement_completed'
} as const;

export type MessageType = typeof MESSAGE_TYPES[keyof typeof MESSAGE_TYPES];

export type FeedbackType = 'good' | 'bad';

export type BasePusherPayload = {
  id: string;
  trip_id: number;
  direction: Direction;
  text: string;
  created_at: string;
};

export type SummaryItem = {
  category: 'outward' | 'homeward' | 'hotel';
  label: string;
  text: string;
  price: number | null;
  priceWithTax: number | null;
};

export type RawSummaryItem = {
  category: 'outward' | 'homeward' | 'hotel';
  label: string;
  text: string;
  price: number | null;
  price_with_tax: number | null;
};

export type Summary = {
  items: SummaryItem[];
  totalPrice: number;
  totalPriceWithTax: number;
  hasSelection: boolean;
};

export type RawSummary = {
  items: RawSummaryItem[];
  total_price: number;
  total_price_with_tax: number;
  has_selection: boolean;
};

export type InsightMetric = {
  label: string;
  value: string;
};

export type InsightColumn = {
  key: string;
  label: string;
};

export type InsightTable = {
  columns: InsightColumn[];
  rows: Record<string, string>[];
  highlightedDates?: string[];
};

export type RawInsightTable = {
  columns: InsightColumn[];
  rows: Record<string, string>[];
  highlighted_dates?: string[];
};

export type RawSearchSupportInsight = {
  title: string;
  summary: string;
  metrics: InsightMetric[];
  table?: RawInsightTable;
  next_action?: string;
  coverage?: string;
  components?: Record<string, unknown>;
  missing_requirements?: string[];
};

export type SearchSupportInsight = {
  title: string;
  summary: string;
  metrics: InsightMetric[];
  table?: InsightTable;
  nextAction?: string;
  coverage?: string;
  components?: Record<string, unknown>;
  missingRequirements?: string[];
};

export type RawUnsupportedRequest = {
  title: string;
  supported_products: string[];
  description: string;
  dashboard_path: string;
  dashboard_label: string;
};

export type UnsupportedRequest = {
  title: string;
  supportedProducts: string[];
  description: string;
  dashboardPath: string;
  dashboardLabel: string;
};

export type RawCalendarConnectionPrompt = {
  title: string;
  description: string;
  action_path: string;
  action_label: string;
};

export type CalendarConnectionPrompt = {
  title: string;
  description: string;
  actionPath: string;
  actionLabel: string;
};

export type RawCalendarContextSummaryDay = {
  date: string;
  status: 'available' | 'busy';
  blocking_event_count: number;
  timed_events_count: number;
  all_day_blockers: string[];
  total_busy_minutes: number;
  longest_free_slot_minutes: number;
  busy_summaries: string[];
};

export type CalendarContextSummaryDay = {
  date: string;
  status: 'available' | 'busy';
  blockingEventCount: number;
  timedEventsCount: number;
  allDayBlockers: string[];
  totalBusyMinutes: number;
  longestFreeSlotMinutes: number;
  busySummaries: string[];
};

export type RawCalendarContextSummary = {
  period: {
    label: string;
    from: string;
    to: string;
  };
  candidate_dates: string[];
  suggested_dates: string[];
  days: RawCalendarContextSummaryDay[];
};

export type CalendarContextSummary = {
  period: {
    label: string;
    from: string;
    to: string;
  };
  candidateDates: string[];
  suggestedDates: string[];
  days: CalendarContextSummaryDay[];
};

export type ProposalBundleItem = {
  id: number;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  price: number | null;
  priceText: string | null;
  aiHighlightTags: string[] | null;
  metadata: Record<string, unknown> | null;
};

export type ProposalBundle = {
  id: number;
  category: string;
  metadata: Record<string, unknown> | null;
  items: ProposalBundleItem[];
};

export type ChatMessage = {
  id: string;
  direction: Direction;
  text: string;
  createdAt: string;
  type?: MessageType;
  proposalBundles?: ProposalBundle[];
  proposalBundleIds?: number[];
  proposalItemIds?: number[];
  summary?: Summary;
  insight?: SearchSupportInsight;
  unsupportedRequest?: UnsupportedRequest;
  calendarConnectionPrompt?: CalendarConnectionPrompt;
  calendarContextSummary?: CalendarContextSummary;
  editable?: boolean;
  orderId?: number;
  approvalRequired?: boolean;
  isNewlyCompleted?: boolean;
  inputDisabled?: boolean;
  proposalChangeDisabled?: boolean;
  isNew?: boolean;
  feedback: FeedbackType | null;
};

export type PayloadByType = {
  [MESSAGE_TYPES.PROPOSAL]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.PROPOSAL;
    direction: 'ai';
    ai_message_id: number;
    bundle_ids: number[];
    item_ids: number[];
  };
  [MESSAGE_TYPES.SEARCH_SUPPORT_INSIGHT]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.SEARCH_SUPPORT_INSIGHT;
    direction: 'ai';
    insight: RawSearchSupportInsight;
  };
  [MESSAGE_TYPES.UNSUPPORTED_REQUEST]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.UNSUPPORTED_REQUEST;
    direction: 'ai';
    unsupported_request: RawUnsupportedRequest;
  };
  [MESSAGE_TYPES.CALENDAR_CONNECTION_PROMPT]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.CALENDAR_CONNECTION_PROMPT;
    direction: 'ai';
    calendar_connection_prompt: RawCalendarConnectionPrompt;
  };
  [MESSAGE_TYPES.CALENDAR_CONTEXT_SUMMARY]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.CALENDAR_CONTEXT_SUMMARY;
    direction: 'ai';
    calendar_context_summary: RawCalendarContextSummary;
  };
  [MESSAGE_TYPES.SUMMARY]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.SUMMARY;
    direction: 'ai';
    summary: RawSummary;
  };
  [MESSAGE_TYPES.RESERVATION_INFO_FORM]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.RESERVATION_INFO_FORM;
    editable?: boolean;
  };
  [MESSAGE_TYPES.MESSAGE]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.MESSAGE;
  };
  [MESSAGE_TYPES.ERROR]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.ERROR;
  };
  [MESSAGE_TYPES.SYSTEM]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.SYSTEM;
  };
  [MESSAGE_TYPES.OPTIMISTIC]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.OPTIMISTIC;
  };
  [MESSAGE_TYPES.ARRANGEMENT_CONFIRMATION]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.ARRANGEMENT_CONFIRMATION;
    direction: 'ai';
    summary: RawSummary;
  };
  [MESSAGE_TYPES.ARRANGEMENT_COMPLETED]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.ARRANGEMENT_COMPLETED;
    direction: 'ai';
    order_id: number;
    approval_required: boolean;
  };
  [MESSAGE_TYPES.RESERVATION_INFO_CONFIRMED]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.RESERVATION_INFO_CONFIRMED;
    direction: 'ai';
    input_disabled: boolean;
    proposal_change_disabled: boolean;
  };
};

export type PusherPayload = PayloadByType[keyof PayloadByType];
